// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The chunk walker.
 *
 * Three properties separate this from every off-the-shelf PNG chunk reader, and
 * each one exists because of a file this tool is specifically for:
 *
 * 1. IT WALKS TO END-OF-FILE, NOT TO IDAT. PNG 3rd ed Table 7 gives tEXt, zTXt,
 *    iTXt and tIME the ordering constraint "None" — they may legally appear
 *    after the image data — and ComfyUI's own animated-PNG writer passes
 *    `after_idat=True` when it adds its `comf` chunk. A walker that stops at
 *    IDAT reports "no recipe" on a file that plainly has one.
 *
 * 2. A BAD CRC IS RECORDED, NOT THROWN. The usual library behaviour is to throw
 *    on the first CRC mismatch, which means a file whose IDAT got one byte
 *    mangled in transit loses its perfectly intact text chunks too. Damaged
 *    files are this tool's core population, so every chunk carries a `crcOk`
 *    flag and the walk continues.
 *
 * 3. EVERY LENGTH IS BOUNDS-CHECKED BEFORE IT IS BELIEVED. A chunk length is an
 *    attacker-controlled 32-bit number. Allocating from it unchecked is how a
 *    0xF0000000 length hangs a tab; here it stops the walk and is reported.
 *
 * Bytes after IEND are captured rather than dropped: they are legal, they are
 * common, and a reader that silently discards them cannot tell the user that
 * ComfyUI's own reader will throw on a 1-to-3-byte one.
 */

import { crc32 } from './crc32.ts';
import type { ChunkFlags, PngChunk, PngParse } from '../types.ts';

export const PNG_SIGNATURE = Uint8Array.from([137, 80, 78, 71, 13, 10, 26, 10]);

export function hasPngSignature(bytes: Uint8Array): boolean {
  if (bytes.length < 8) return false;
  for (let i = 0; i < 8; i++) if (bytes[i] !== PNG_SIGNATURE[i]) return false;
  return true;
}

function readUint32(bytes: Uint8Array, at: number): number {
  return (
    ((bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3]) >>> 0
  );
}

function readType(bytes: Uint8Array, at: number): string | null {
  let out = '';
  for (let i = 0; i < 4; i++) {
    const c = bytes[at + i];
    // A chunk type is four ASCII letters. Anything else means the walk has lost
    // its place, which is worth reporting rather than papering over.
    if (!((c >= 65 && c <= 90) || (c >= 97 && c <= 122))) return null;
    out += String.fromCharCode(c);
  }
  return out;
}

/** Decode the four case bits that PNG 3rd ed §5.4 encodes in the type letters. */
export function chunkFlags(type: string): ChunkFlags {
  const lower = (i: number) => {
    const c = type.charCodeAt(i);
    return c >= 97 && c <= 122;
  };
  return {
    ancillary: lower(0),
    private: lower(1),
    // A conforming file has this bit CLEAR, i.e. an uppercase third letter.
    // ComfyUI's `comf` has it set, which is non-conforming and harmless — but
    // it is worth reporting rather than treating as a parse failure.
    reservedSet: lower(2),
    safeToCopy: lower(3),
  };
}

export function parsePng(bytes: Uint8Array): PngParse {
  const base: PngParse = {
    ok: false,
    chunks: [],
    trailerStart: bytes.length,
    trailerLength: 0,
    truncated: false,
    missingIend: true,
    totalLength: bytes.length,
  };

  if (!hasPngSignature(bytes)) {
    return { ...base, error: 'This file does not start with a PNG signature.' };
  }

  const chunks: PngChunk[] = [];
  let at = 8;
  let sawIdat = false;
  let sawIend = false;
  let truncated = false;
  let index = 0;

  while (at + 8 <= bytes.length) {
    const length = readUint32(bytes, at);
    const type = readType(bytes, at + 4);
    if (type === null) {
      truncated = true;
      break;
    }
    // length | type(4) | data | crc(4). Everything after the length field must
    // fit inside what is left of the file.
    if (length > bytes.length - at - 12) {
      truncated = true;
      break;
    }
    const dataStart = at + 8;
    const declaredCrc = readUint32(bytes, dataStart + length);
    const actualCrc = crc32(bytes, at + 4, dataStart + length);
    chunks.push({
      index,
      type,
      offset: at,
      dataStart,
      dataLen: length,
      declaredCrc,
      crcOk: declaredCrc === actualCrc,
      afterIdat: sawIdat,
    });
    index++;
    if (type === 'IDAT') sawIdat = true;
    at = dataStart + length + 4;
    if (type === 'IEND') {
      sawIend = true;
      break;
    }
  }

  if (chunks.length === 0) {
    return { ...base, truncated, error: 'No readable chunks after the PNG signature.' };
  }

  return {
    ok: true,
    chunks,
    trailerStart: at,
    trailerLength: Math.max(0, bytes.length - at),
    truncated,
    missingIend: !sawIend,
    totalLength: bytes.length,
  };
}

export function findChunks(parse: PngParse, type: string): PngChunk[] {
  return parse.chunks.filter((c) => c.type === type);
}

export function chunkData(bytes: Uint8Array, chunk: PngChunk): Uint8Array {
  return bytes.subarray(chunk.dataStart, chunk.dataStart + chunk.dataLen);
}

/** Human-readable geometry from IHDR, or null if there is no usable one. */
export function readIhdr(
  bytes: Uint8Array,
  parse: PngParse,
): { width: number; height: number; bitDepth: number; colourType: number; interlace: number } | null {
  const ihdr = parse.chunks.find((c) => c.type === 'IHDR');
  if (!ihdr || ihdr.dataLen < 13) return null;
  const d = ihdr.dataStart;
  return {
    width: readUint32(bytes, d),
    height: readUint32(bytes, d + 4),
    bitDepth: bytes[d + 8],
    colourType: bytes[d + 9],
    interlace: bytes[d + 12],
  };
}
